import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Briefcase, ChevronRight, Building2, Users, Truck, ShieldAlert } from "lucide-react";

const ROLE_META = {
  surveyor: { label: "Surveyor", icon: Users, color: "#00A86B", desc: "Inspect vehicles, file survey reports." },
  workshop: { label: "Workshop", icon: Building2, color: "#003DA5", desc: "Coordinate cashless repairs." },
  partner:  { label: "Partner",  icon: Truck,     color: "#FF7A00", desc: "Tow & ancillary services." },
};

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/partner/claims")
      .then((r) => setClaims(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load claims"))
      .finally(() => setLoading(false));
  }, []);

  const role = user?.role;
  const meta = ROLE_META[role];

  if (role && !["partner", "surveyor", "workshop", "admin"].includes(role)) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4" data-testid="partner-no-access">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-md">
          <ShieldAlert className="w-12 h-12 text-[#FF7A00] mx-auto mb-3" />
          <h2 className="font-heading font-bold text-xl text-slate-900">Partner access required</h2>
          <p className="text-sm text-slate-600 mt-2">Your account is registered as <span className="font-semibold">{role}</span>. Contact an admin to upgrade your role to partner, surveyor or workshop.</p>
          <Link to="/dashboard" className="mt-5 inline-block bg-[#003DA5] text-white rounded-lg px-5 py-2.5 font-semibold text-sm">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F8FAFC] py-10" data-testid="partner-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF7A00] mb-2">Partner Console</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              {meta ? `${meta.label} Dashboard` : "Partner Dashboard"}
            </h1>
            <p className="text-slate-600 mt-1">{meta?.desc || "Operate claims assigned to you."}</p>
          </div>
          {meta && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200">
              <meta.icon className="w-4 h-4" style={{ color: meta.color }} />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{meta.label}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Stat label="Assigned Claims" value={claims.length} icon={Briefcase} color="#003DA5" testid="stat-assigned" />
          <Stat label="In Progress" value={claims.filter((c) => !c.current_status?.includes("Closed")).length} icon={Briefcase} color="#FF7A00" testid="stat-progress" />
          <Stat label="This Week" value={claims.filter((c) => {
            const d = new Date(c.created_at);
            return (Date.now() - d.getTime()) < 7 * 24 * 60 * 60 * 1000;
          }).length} icon={Briefcase} color="#00A86B" testid="stat-week" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="font-heading font-bold text-lg text-slate-900">Active claims for you</h2>
          </div>
          {error && (
            <div className="p-6 text-center text-red-600 text-sm" data-testid="partner-error">{error}</div>
          )}
          {loading ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : claims.length === 0 ? (
            <div className="p-10 text-center" data-testid="partner-empty">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-600 font-semibold">No active claims assigned yet</p>
              <p className="text-sm text-slate-500 mt-1">New claims at the right status will appear here automatically.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {claims.map((c) => (
                <li key={c.claim_id} className="px-6 py-4 hover:bg-slate-50 flex items-center justify-between gap-4" data-testid={`partner-claim-${c.claim_id}`}>
                  <div>
                    <div className="font-mono text-sm font-bold text-slate-900">{c.claim_id}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {c.vehicle?.make} {c.vehicle?.model} · {c.vehicle?.reg_no} · {c.insurance?.insurer}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">Customer: {c.customer?.name} ({c.customer?.mobile})</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#FFF2E5] text-[#FF7A00]">
                      {c.current_status}
                    </span>
                    <Link to={`/claim/${c.claim_id}`} className="text-[#003DA5] hover:underline text-xs font-semibold flex items-center gap-1">
                      View <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${color}15`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-heading font-extrabold text-2xl text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
